import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Calendar, Loader2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultCMS = {
  hero: {
    headline1: "Clutter",
    headline2: "News",
    description: "Expert advice on junk removal, decluttering, storage tips, and more from the Overhaul Ops team."
  },
  intro: {
    title: "Latest Posts",
    subtitle: "Stay informed with our latest articles"
  }
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cms, setCms] = useState(defaultCMS);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchCMS = async () => {
      try {
        const response = await axios.get(`${API}/cms/page/blog`);
        if (response.data?.length > 0) {
          const content = { ...defaultCMS };
          response.data.forEach(item => {
            if (item.section && item.content) {
              content[item.section] = { ...content[item.section], ...item.content };
            }
          });
          setCms(content);
        }
      } catch (e) { /* use defaults */ }
    };
    
    fetchPosts();
    fetchCMS();
  }, []);

  return (
    <div data-testid="blog-page" className="pt-20">
      <SEOHead
        title="Clutter News - Junk Removal Tips & Advice"
        description="Expert advice on junk removal, decluttering, storage tips, and more from the Overhaul Ops team in DFW."
        keywords="junk removal blog, decluttering tips, cleanout advice, DFW junk removal news"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }]}
      />
      {/* Hero */}
      <section className="py-20 md:py-32 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              Our Blog
            </span>
            <h1 
              className="text-5xl md:text-7xl font-black text-white uppercase leading-none mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.02em' }}
            >
              {cms.hero.headline1} <span className="text-[#b51a00]">{cms.hero.headline2}</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {cms.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 bg-[#f4f4f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#b51a00]" />
            </div>
          ) : posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card key={post.id} className="border-0 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  {post.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.image_url} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Recently'}</span>
                    </div>
                    <h2 
                      className="text-xl font-bold mb-3 line-clamp-2"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <Link to={`/blog/${post.slug}`}>
                      <Button 
                        variant="outline"
                        className="border-[#b51a00] text-[#b51a00] hover:bg-[#b51a00] hover:text-white"
                      >
                        Read More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No blog posts yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#b51a00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Need Junk Removal?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get a free quote today from DFW's trusted professionals.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
            >
              Get Your Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
